import { useState, useEffect, useMemo } from "react"
import type {
  VisibilityState,
  ColumnSizingState,
  ColumnOrderState,
  OnChangeFn,
  ColumnDef,
} from "@tanstack/react-table"

const STORAGE_KEY = "GlowTablePreferences"

type TablePreferences = {
  columnVisibility?: VisibilityState
  columnSizing?: ColumnSizingState
  columnOrder?: ColumnOrderState
}

// Helper to get all table preferences
const getTablePreferences = (): Record<string, TablePreferences> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch (error) {
    console.error("Failed to load table preferences:", error)
    return {}
  }
}

// Helper to save preferences for a specific table
const saveTablePreferences = (
  tableId: string,
  preferences: TablePreferences,
) => {
  try {
    const allPreferences = getTablePreferences()
    allPreferences[tableId] = {
      ...allPreferences[tableId],
      ...preferences,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allPreferences))
  } catch (error) {
    console.error(`Failed to save preferences for ${tableId}:`, error)
  }
}

interface UseColumnConfigProps<TData> {
  tableId: string
  columns: ColumnDef<TData>[]
  initialVisibility?: VisibilityState
  initialSizing?: ColumnSizingState
  initialOrder?: ColumnOrderState
}

export const useColumnConfig = <TData>({
  tableId,
  columns,
  initialVisibility = {},
  initialSizing = {},
  initialOrder,
}: UseColumnConfigProps<TData>) => {
  // Extract valid column IDs from the column definitions
  const validColumnIds = useMemo(() => {
    return columns
      .map((col) => {
        // Try to get column ID from different possible sources
        if ("id" in col && typeof col.id === "string") return col.id
        if ("accessorKey" in col && typeof col.accessorKey === "string") {
          return col.accessorKey
        }
        return null
      })
      .filter((id): id is string => id !== null)
  }, [columns])

  // Load from localStorage with validation
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const allPreferences = getTablePreferences()
        const tablePrefs = allPreferences[tableId]

        if (tablePrefs?.columnVisibility) {
          const parsed = tablePrefs.columnVisibility

          // Validate: only keep columns that still exist
          const validated: VisibilityState = {}
          for (const [columnId, isVisible] of Object.entries(parsed)) {
            if (validColumnIds.includes(columnId)) {
              validated[columnId] = isVisible
            }
          }

          return validated
        }
      } catch (error) {
        console.error(`Failed to load column visibility for ${tableId}:`, error)
      }
      return initialVisibility
    },
  )

  // Load column sizing from localStorage with validation
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
    try {
      const allPreferences = getTablePreferences()
      const tablePrefs = allPreferences[tableId]

      if (tablePrefs?.columnSizing) {
        const parsed = tablePrefs.columnSizing

        // Validate: only keep columns that still exist
        const validated: ColumnSizingState = {}
        for (const [columnId, size] of Object.entries(parsed)) {
          if (validColumnIds.includes(columnId)) {
            validated[columnId] = size
          }
        }

        return validated
      }
    } catch (error) {
      console.error(`Failed to load column sizing for ${tableId}:`, error)
    }
    return initialSizing
  })

  // Load column order from localStorage with validation
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    try {
      const allPreferences = getTablePreferences()
      const tablePrefs = allPreferences[tableId]

      if (tablePrefs?.columnOrder && Array.isArray(tablePrefs.columnOrder)) {
        const savedOrder = tablePrefs.columnOrder

        // Validate: only keep columns that still exist
        const validOrder = savedOrder.filter((id) =>
          validColumnIds.includes(id),
        )

        // Add new columns (not in saved order) at the end
        const newColumns = validColumnIds.filter(
          (id) => !validOrder.includes(id),
        )

        return [...validOrder, ...newColumns]
      }
    } catch (error) {
      console.error(`Failed to load column order for ${tableId}:`, error)
    }
    return initialOrder ?? validColumnIds
  })

  // Persist to localStorage whenever visibility changes
  useEffect(() => {
    saveTablePreferences(tableId, { columnVisibility })
  }, [columnVisibility, tableId])

  // Persist to localStorage whenever sizing changes
  useEffect(() => {
    saveTablePreferences(tableId, { columnSizing })
  }, [columnSizing, tableId])

  // Persist to localStorage whenever order changes
  useEffect(() => {
    saveTablePreferences(tableId, { columnOrder })
  }, [columnOrder, tableId])

  const onColumnVisibilityChange: OnChangeFn<VisibilityState> = (
    updaterOrValue,
  ) => {
    const newVisibility =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnVisibility)
        : updaterOrValue

    setColumnVisibility(newVisibility)
  }

  const onColumnSizingChange: OnChangeFn<ColumnSizingState> = (
    updaterOrValue,
  ) => {
    const newSizing =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnSizing)
        : updaterOrValue

    setColumnSizing(newSizing)
  }

  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = (
    updaterOrValue,
  ) => {
    const newOrder =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnOrder)
        : updaterOrValue

    setColumnOrder(newOrder)
  }

  return {
    columnVisibility,
    onColumnVisibilityChange,
    columnSizing,
    onColumnSizingChange,
    columnOrder,
    onColumnOrderChange,
  }
}

// Utility function to clear all table preferences
export const clearAllTablePreferences = () => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error("Failed to clear all table preferences:", error)
  }
}

// Utility function to clear preferences for a specific table
export const clearTablePreferences = (tableId: string) => {
  try {
    const allPreferences = getTablePreferences()
    delete allPreferences[tableId]
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allPreferences))
  } catch (error) {
    console.error(`Failed to clear preferences for ${tableId}:`, error)
  }
}
